import { action, computed, IObservableArray, observable } from "mobx";
import * as DocumentReader from "../DocumentReader";
import { DialogStore } from "../Stores/DialogStore";
import { Strings } from "../Stores/StringsStore";
import { DocumentSearchStore } from "./DocumentSearchStore";

export class TypeAheadStore {

	constructor(public documentSearchStore?: DocumentSearchStore) {

	}
	/**
	* stores type ahead suggestion from API
	*/
	@observable suggestions: DocumentReader.TypeAheadSuggestion[] = [];
	/**
	* keep current suggestion position
	*/
	@observable suggestionIndex: number = -1;
	/**
	 * Search term for type ahead
	 */
	@observable searchTerm: string = "";
	/**
	 * To decide whether we need to show Suggestion List or Not
	 */
	showSuggestions: boolean = true;

	@observable isLoading: boolean = false;
	/**
	 * Callback function for typeahead component when search term changes other than from reader and home page
	 */
	searchTermChangeCallback?: (searchTerm: string) => void;

	searchTimeoutHandle: number;

	undoBuffer: IObservableArray<string> = observable.array([]);

	undoIndex: number = 0;

	@computed get undoBufferLength(): number {
		return this.undoBuffer.length;
	}

	/**
	 * undo handler (TODO) implement with searchInputTermStore Iterator. 
	 */
	@action undoRedoHandler = (index: number) => {
		if (this.undoIndex + index < this.undoBufferLength - 1) {
			this.undoIndex += index;
			let indexOfTerm = (this.undoBufferLength - 1) - this.undoIndex;
			this.searchTerm = this.undoBuffer[indexOfTerm];
		} else {
			this.searchTerm = "";
		}
	}

	/**
	 * copy the search term to undo buffer
	 */
	@action copyToUndoBuffer = () => {
		//the last item in the list is already part of the list
		let isDuplicateLastTerm = this.undoBufferLength > 0 && this.undoBuffer[this.undoBufferLength - 1] === this.searchTerm;
		if (!isDuplicateLastTerm) {
			this.undoBuffer.push(this.searchTerm);
		}
	}

	/**
	 *	Load suggestions
	 */
	@action loadSuggestions = () => {
		if (this.searchTerm && this.showSuggestions) {
			this.isLoading = true;
			let query = this.createQuery();
			DocumentReader.getTypeAheadAsync(query)
				.then(result => {
					this.setResult(result);
					this.isLoading = false;
				})
				.catch(error => {
					DialogStore.showError(Strings.general.errorDialogTitle, error);
					this.isLoading = false;
				});
		}
		else if (this.searchTermChangeCallback) {
			this.searchTermChangeCallback(this.searchTerm);
			this.isLoading = false;
		}
		else {
			this.resetSuggestions();
			this.isLoading = false;
		}
	}

	/**
	*	Reset params used for Type Ahead suggestions
	*/
	@action resetSuggestions = () => {
		this.setResult(null);
	}

	@action private setResult(result: DocumentReader.TypeAheadSuggestion[]) {
		this.suggestionIndex = -1;
		this.suggestions = (result && result.length > 0) ? result : [];
	}

	/**
	 * Creates the current TypeAheadQuery object for triggering the type ahead search.
	 */
	private createQuery(): DocumentReader.TypeAheadQuery {
		let query: DocumentReader.TypeAheadQuery = new DocumentReader.TypeAheadQuery();

		query.fileVariants = null;
		query.searchExpression = this.searchTerm;
		if (this.documentSearchStore) {
			query.filters = this.documentSearchStore.documentFilter.getDocumentFilters();
		}		
		return query;
	}
}
